import React, { useMemo } from "react";
import _filter from "lodash/filter";
import _keyBy from "lodash/keyBy";
import _groupBy from "lodash/groupBy";
import _sortBy from "lodash/sortBy";
import _map from "lodash/map";
import _find from "lodash/find";
import _flow from "lodash/flow";
import { format } from "date-fns";
import { useTranslation, TFunction } from "i18n";
import { DatesFormat } from "shared-types";
import { useGetCredits } from "pages/Profile/hooks/useGetCredits";
import Credit from "../../../../../../models/Credits/Credit";
import CreditHeading from "../../../../../../models/Credits/CreditHeading";

export const mapCredits = (
  credits: Credit[],
  creditsHeadings: CreditHeading[],
  t: TFunction
) => {
  const otherLabel = t("common:label.other");

  const activeHeadingsMap = _keyBy(
    _filter(creditsHeadings, { isHidden: false }),
    "creditType.id"
  );

  const getCategoryName = (credit: Credit) =>
    activeHeadingsMap[credit.typeId]?.creditType.name || otherLabel;

  const groupedCredits = _groupBy(credits, getCategoryName);

  const formatGroup = (items: CreditHeading[], name: string) => {
    const heading = _find(creditsHeadings, { creditType: { name } });

    return {
      name,
      data: _sortBy(items, "position"),
      weight: name === otherLabel ? Infinity : heading?.position,
    };
  };

  return _flow([
    (groups) => _map(groups, formatGroup),
    (mapped) => _sortBy(mapped, "weight"),
    (sorted) => _map(sorted, (group) => [group.name, group.data]),
  ])(groupedCredits);
};

const CreditsPrint = () => {
  const { t } = useTranslation();
  const { credits, creditsHeadings } = useGetCredits();

  const creditTable = useMemo(
    () =>
      !!credits?.length && !!creditsHeadings?.length
        ? mapCredits(credits, creditsHeadings, t)
        : [],
    [credits, creditsHeadings, t]
  );

  return (
    <div className="space-y-1">
      {creditTable.map(([category, items]) => (
        <div key={category} className="break-inside-auto">
          <h3 className="text-xxs">{category}</h3>
          <table className="w-full table-fixed border-collapse text-left">
            <thead>
              <tr className="bg-grey-fog-1 text-xxxs font-medium">
                <th className="w-1/3 py-1 pl-1">
                  {t("performer:profile.credits.productionRole")}
                </th>
                <th className="w-1/4 py-1">
                  {t("performer:profile.credits.director")}
                </th>
                <th className="w-1/12 py-1">
                  {t("performer:profile.credits.date")}
                </th>
                <th className="w-1/12 py-1">
                  {t("performer:profile.credits.type")}
                </th>
                <th className="w-1/4 py-1">
                  {t("performer:profile.credits.company")}
                </th>
              </tr>
            </thead>
            <tbody>
              {items.map((credit) => {
                const dateFrom = credit.dateFrom
                  ? format(new Date(credit.dateFrom), DatesFormat.yearFormat)
                  : "";
                const dateTo = credit.dateTo
                  ? format(new Date(credit.dateTo), DatesFormat.yearFormat)
                  : "";
                const dateValue =
                  dateFrom && dateTo
                    ? `${dateFrom} - ${dateTo}`
                    : dateFrom || dateTo;

                return (
                  <tr key={credit.id}>
                    <td className="py-1 pr-2">
                      <p className="mb-1 font-bold">{credit.name}</p>
                      <span>{credit.role}</span>
                    </td>
                    <td className="py-1 font-medium text-gray-700">
                      {credit.director}
                    </td>
                    <td className="py-1">{dateValue}</td>
                    <td className="py-1">{credit.type}</td>
                    <td className="py-1">{credit.company}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
};

export default CreditsPrint;
